import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TransferHttpService } from '../../../services/transfer-http/transfer-http.service';
import { PermissionsService } from '../../../services/permissions/permissions.service';

import {
  Organizations, OrganizationType, Resources, ScheduleItemStatus, ScheduleItemType, Clearance, Grade, MinPermPos
} from '../schCommon';
import { AllSites, OpsTaskCodes, SchFlightAuthType, SchGrade, SchLodgingAuthType, SchRentalCarAuthType, SchTravelRequestConfig, SchTravelRequestEmails, SchTravelerType } from '../../../../../src/models';
import { AimsCommonTableFilterType, AimsCommonTableHeader, AimsCommonTableHeaderType, AimsCommonTablePermissions, TableRowDelete, TableRowUpdate } from '@aims/common-components';
import { Validators } from '@angular/forms';
import { Observable } from 'rxjs';

type SchGridPermOptions = { create?: boolean, read?: boolean, update?: boolean, delete?: boolean, export?: boolean };

@Component({
  selector: 'app-sch-config',
  templateUrl: './sch-config.component.html',
  styleUrls: ['./sch-config.component.scss']
})
export class SchConfigComponent implements OnInit {

  constructor(private transferHttp: TransferHttpService,
    private router: Router,
    public permissions: PermissionsService) { }

  user = this.transferHttp.getCache('user');

  trvlRequestConfig: SchTravelRequestConfig;

  //Used for aims-lookup
  gradeLookupColumns:AimsCommonTableHeader[] = [
    { key: 'grade', label: 'Grade', filter: true, type:AimsCommonTableHeaderType.STRING },
    { key: 'rank', label: 'Rank(AF/Other,Navy/CG,Army,Marines)', filter: true, type:AimsCommonTableHeaderType.STRING },
  ];

  //Grids
  newOrganization() {
    return new Organizations();
  }

  newOrganizationType() {
    return new OrganizationType();
  }

  newResource() {
    let newRes = new Resources();
    newRes.createdOn = new Date();
    newRes.createdById = this.user.employeeId;
    return newRes;
  }

  newClearance() {
    return new Clearance();
  }

  newSite() {
    return new AllSites();
  }

  newGrade() {
    return new Grade();
  }

  newMinPermPos() {
    return new MinPermPos();
  }

  newScheduleItemStatus() {
    return new ScheduleItemStatus();
  }
  newScheduleItemType() {
    return new ScheduleItemType();
  }

  // data sources
  travelRequestEmails = this.transferHttp.getSingle('Sch/GetTravelRequestEmail');
  organizations = this.transferHttp.getSingle('Sch/GetOrganizations');
  organizationType = this.transferHttp.getSingle('Sch/GetOrganizationType');
  resources = this.transferHttp.getSingle('Sch/GetResources');
  clearance = this.transferHttp.getSingle('Sch/GetClearance');
  grade = this.transferHttp.getSingle('Sch/GetGrade');
  minPermPos = this.transferHttp.getSingle('Sch/GetMinPermPos');
  scheduleItemStatus = this.transferHttp.getSingle('Sch/GetScheduleItemStatus');
  scheduleItemType = this.transferHttp.getSingle('Sch/GetScheduleItemType');
  travelTaskCodes = this.transferHttp.getSingle('Sch/GetTravelTaskCodes');
  travelSites = this.transferHttp.getSingle('Sch/GetScheduleAndTravelSites');
  companySites = this.transferHttp.getSingle('Sch/GetAllCompanyOrSanSites');
  genderType = this.transferHttp.getSingle('HR/GetGenderType');
  jobTitles = this.transferHttp.getSingle('HR/GetJobTitles');
  lodgingAuthTypeValues = this.transferHttp.getSingle('Sch/GetLodgingAuthType');
  rentalCarAuthTypeValues = this.transferHttp.getSingle('Sch/GetRentalCarAuthType');
  flightAuthTypeValues = this.transferHttp.getSingle('Sch/GetFlightAuthType');
  travelerTypeValues = this.transferHttp.getSingle('Sch/GetTravelerType');

  //Spinner
  spinnerText: string = "Loading...";
  showSpinner: boolean = false;

  // AimsCommonTable setup
  private createGridPerms(options?: SchGridPermOptions) : Observable<AimsCommonTablePermissions> {
    options = { delete: false, export: false, ...options };
    return this.permissions.getDataGridPerms$(this.permissions.ComponentID.SchedulingConfig, options);
  }
  tablePermissions: Observable<AimsCommonTablePermissions> = this.createGridPerms();
  orgsTablePermissions: Observable<AimsCommonTablePermissions> = this.tablePermissions;
  orgsTableHeader: AimsCommonTableHeader[] = [
    { key: 'editButton', label: 'Edit', type: 'editButton', filter: false },
    { key: 'organizationName', label: 'Name', type: 'string', width: 200, validatorOrOpts: [Validators.required]},
    { key: 'govBilled', label: 'Gov Billed', type: 'boolean', filterType: 'boolean' },
    { key: 'companyBilled', label: 'Company Billed', type: 'boolean',  filterType: 'boolean'},
    { key: 'organizationTypeId', label: 'Type', type: 'dynamic', filterType: 'dynamic', width: 100, validatorOrOpts: [Validators.required],
      events: { filterAndSortCompare: (term, el) => term === el.organizationTypeId } },
    { key: 'address', label: 'Address', type: 'string', },
    { key: 'email', label: 'Email', type: 'string', },
    { key: 'contactPhone', label: 'Contact #', type: 'dynamic', },
  ];
  defaultOrganization: Organizations = this.newOrganization();


  orgTypeTablePermissions: Observable<AimsCommonTablePermissions> = this.tablePermissions;
  orgTypeTableHeader: AimsCommonTableHeader[] = [
    { key: 'editButton', label: 'Edit', type: 'editButton', filter: false },
    { key: 'organizationTypeCode', label: 'Type', type: 'string', width: 200, validatorOrOpts: [Validators.required, Validators.maxLength(3)]},
  ];
  defaultOrganizationType: OrganizationType = this.newOrganizationType();

  resTablePermissions: Observable<AimsCommonTablePermissions> = this.tablePermissions;
  resTableHeader: AimsCommonTableHeader[] = [
    { key: 'editButton', label: 'Edit', type: 'editButton', filter: false },
    { key: 'active', label: 'Active', type: 'boolean', filterType: 'boolean'},
    { key: 'fullName', label: 'Name', type: 'string', editable: false },
    { key: 'lastName', label: 'Last Name', type: 'string', validatorOrOpts: [Validators.required, Validators.maxLength(50)]},
    { key: 'firstName', label: 'First Name', type: 'string', validatorOrOpts: [Validators.maxLength(50)]},
    { key: 'middleName', label: 'Middle Name', type: 'string', validatorOrOpts: [Validators.maxLength(50)]},
    { key: 'gradeId', label: 'Grade', type: 'dynamic', filterType: 'dynamic', width: 100, validatorOrOpts: [Validators.required],
      events: { filterAndSortCompare: (term, el) => term === el.gradeId} },
    { key: 'clearanceId', label: 'Clearance', type: 'dynamic', filterType: 'dynamic', width: 100, validatorOrOpts: [Validators.required],
      events: { filterAndSortCompare: (term, el) => term === el.clearanceId} },
    { key: 'genderTypeId', label: 'Gender', type: 'dynamic', filterType: 'dynamic', width: 100, validatorOrOpts: [Validators.required],
      events: { filterAndSortCompare: (term, el) => term === el.genderTypeId} },
    { key: 'organizationId', label: 'Organization', type: 'dynamic', filterType: 'dynamic', width: 100, validatorOrOpts: [Validators.required],
      events: { filterAndSortCompare: (term, el) => term === el.organizationId} },
    { key: 'isCarrier', label: 'Carrier', type: 'boolean', filterType: 'boolean'},
    { key: 'isVip', label: 'VIP', type: 'boolean', filterType: 'boolean'},
  ];
  defaultResource: Resources = this.newResource();

  schedItemTypeTablePermissions: Observable<AimsCommonTablePermissions> = this.tablePermissions;
  schedItemTypeTableHeader: AimsCommonTableHeader[] = [
    { key: 'editButton', label: 'Edit', type: 'editButton', filter: false },
    { key: 'scheduleItemType', label: 'Type', type: 'string', validatorOrOpts: [Validators.required]},
    { key: 'requiresApproval', label: 'Requires Approval', type: 'boolean', filterType: 'boolean', width: 90},
    { key: 'color', label: 'Color', type: 'dynamic', filter: false, width: 200 },
    { key: 'isEmpAndResSiType', label: 'Is All SI Type', type: 'boolean', filterType: 'boolean', width: 90},
    { key: 'isEmployeeSiType', label: 'Is Employee SI Type', type: 'boolean', filterType: 'boolean', width: 90},
    { key: 'govBilledDefault', label: 'Gov Billed', type: 'boolean', filterType: 'boolean', width: 90},
    { key: 'companyBilledDefault', label: 'Company Billed', type: 'boolean', filterType: 'boolean', width: 90},
    { key: 'mealsDefault', label: 'Meals', type: 'boolean', filterType: 'boolean', width: 90},
    { key: 'billetingDefault', label: 'Billeting', type: 'boolean', filterType: 'boolean', width: 90},
    { key: 'icon', label: 'Icon', type: 'dynamic', validatorOrOpts: [Validators.required], width: 200},
  ];
  defaultScheduleItemType: ScheduleItemType = this.newScheduleItemType();

  clearanceTablePermissions: Observable<AimsCommonTablePermissions> = this.tablePermissions;
  clearanceTableHeader: AimsCommonTableHeader[] = [
    { key: 'editButton', label: 'Edit', type: 'editButton', filter: false },
    { key: 'clearanceName', label: 'name', type: 'string', validatorOrOpts: [Validators.required]},
  ];
  defaultClearance: Clearance = this.newClearance();

  sitesTablePermissions: Observable<AimsCommonTablePermissions> = this.tablePermissions;
  sitesTableHeader: AimsCommonTableHeader[] = [
    { key: 'editButton', label: 'Edit', type: 'editButton', filter: false },
    { key: 'siteName', label: 'Name', type: 'string', validatorOrOpts: [Validators.required]},
    { key: 'siteCode', label: 'Code', type: 'string', validatorOrOpts: [Validators.required, Validators.maxLength(3)]},
    { key: 'travelSiteCode', label: 'Travel Site Code', type: 'string', validatorOrOpts: [Validators.required, Validators.maxLength(3)]},
    { key: 'travelMgmtsiteCode', label: 'Travel MGMT Site Code', type: 'string', validatorOrOpts: [Validators.required, Validators.maxLength(3)]},    
    { key: 'sanRequired', label: 'Requires SAN', type: 'boolean', filterType: 'boolean', width: 90},
    { key: 'requiresStep2Approval', label: 'Step 2 App', type: 'boolean', filterType: 'boolean', width: 90},
  ];
  defaultSite: AllSites = this.newSite();

  gradeTableHeader: AimsCommonTableHeader[] = [
    { key: 'editButton', label: 'Edit', type: 'editButton', filter: false },
    { key: 'grade', label: 'Grade', type: 'string', validatorOrOpts: [Validators.required]},
    { key: 'rank', label: 'Rank(AF/Other,Navy/CG,Army,Marines)', type: 'string', validatorOrOpts: [Validators.required]},
  ];
  defaultGrade: Grade = this.newGrade();

  minPermPosTableHeader: AimsCommonTableHeader[] = [
    { key: 'editButton', label: 'Edit', type: 'editButton', filter: false },
    { key: 'siteId', label: 'Site', type: 'dynamic', filterType: 'dynamic', validatorOrOpts: [Validators.required],
      events: { filterAndSortCompare: (term, el) => term === el.siteId} },
    { key: 'jobTitleId', label: 'Type', type: 'dynamic', filterType: 'dynamic', validatorOrOpts: [Validators.required],
      events: { filterAndSortCompare: (term, el) => term === el.jobTitleId} },
    { key: 'minimum', label: 'Minimum', type: 'number', width: 100, validatorOrOpts: [Validators.required, Validators.min(0)]},
  ];
  defaultMinPermPos: MinPermPos = this.newMinPermPos();

  travelTaskCodesTableHeader: AimsCommonTableHeader[] = [
    { key: 'editButton', label: 'Edit', type: 'editButton', filter: false },
    { key: 'taskCode', label: 'Task Code', type: 'string', validatorOrOpts: [Validators.required, Validators.maxLength(200)], },
    { key: 'taskAcctCode', label: 'Account Code', type: 'string', validatorOrOpts: [Validators.required, Validators.maxLength(3)], },
  ];
  defaultTravelTaskCode: OpsTaskCodes = new OpsTaskCodes();

  travelRequestEmailsTableHeader: AimsCommonTableHeader[] = [
    { key: 'editButton', label: 'Edit', type: 'editButton', filter: false },
    { key: 'emailAddress', label: 'Email Address ', type: 'string', validatorOrOpts: [Validators.required], },
    { key: 'sendFrom', label: 'Send From?', type: 'boolean', validatorOrOpts: [Validators.required]},
    { key: 'includeOnInternal', label: 'Internal?', type: 'boolean', validatorOrOpts: [Validators.required]}
  ];
  travelRequestEmailsTablePermissions: Observable<AimsCommonTablePermissions> = this.createGridPerms({ delete: true });
  defaultEmail: SchTravelRequestEmails = new SchTravelRequestEmails();

  lodgingAuthTypeTableHeader: AimsCommonTableHeader[] = [
    { key: 'editButton', label: 'Edit', type: 'editButton', filter: false },
    { key: 'lodgingAuthType', label: 'Lodging Auth Type', type: AimsCommonTableHeaderType.STRING, validatorOrOpts: [Validators.required], },
    { key: 'active', label: 'Active', type: AimsCommonTableHeaderType.BOOLEAN, filterType: AimsCommonTableFilterType.BOOLEAN }
  ];
  lodgingAuthTypeTablePermissions: Observable<AimsCommonTablePermissions> = this.createGridPerms();
  defaultLodgingAuthType: SchLodgingAuthType = new SchLodgingAuthType();

  rentalCarAuthTypeTableHeader: AimsCommonTableHeader[] = [
    { key: 'editButton', label: 'Edit', type: 'editButton', filter: false },
    { key: 'rentalCarAuthType', label: 'Rental Car Auth Type', type: AimsCommonTableHeaderType.STRING, validatorOrOpts: [Validators.required], },
    { key: 'active', label: 'Active', type: AimsCommonTableHeaderType.BOOLEAN, filterType: AimsCommonTableFilterType.BOOLEAN }
  ];
  rentalCarAuthTypeTablePermissions: Observable<AimsCommonTablePermissions> = this.createGridPerms();
  defaultRentalCarAuthType: SchRentalCarAuthType = new SchRentalCarAuthType();

  flightAuthTypeTableHeader: AimsCommonTableHeader[] = [
    { key: 'editButton', label: 'Edit', type: 'editButton', filter: false },
    { key: 'flightAuthType', label: 'Flight Auth Type', type: AimsCommonTableHeaderType.STRING, validatorOrOpts: [Validators.required], },
    { key: 'active', label: 'Active', type: AimsCommonTableHeaderType.BOOLEAN, filterType: AimsCommonTableFilterType.BOOLEAN }
  ];
  flightAuthTypeTablePermissions: Observable<AimsCommonTablePermissions> = this.createGridPerms();
  defaultFlightAuthType: SchFlightAuthType = new SchFlightAuthType();

  travelerTypeTableHeader: AimsCommonTableHeader[] = [
    { key: 'editButton', label: 'Edit', type: 'editButton', filter: false },
    { key: 'travelerType', label: 'Traveler Type', type: AimsCommonTableHeaderType.STRING, validatorOrOpts: [Validators.required, Validators.maxLength(250)], },
    { key: 'travelerTypeCode', label: 'Code', type: AimsCommonTableHeaderType.STRING, validatorOrOpts: [Validators.required, Validators.maxLength(2)], },
    { key: 'clinAbbreviation', label: 'Clin', type: AimsCommonTableHeaderType.STRING, validatorOrOpts: [Validators.maxLength(1)], },
    { key: 'isActive', label: 'Active', type: AimsCommonTableHeaderType.BOOLEAN, filterType: AimsCommonTableFilterType.BOOLEAN }
  ];
  travelerTypeTablePermissions: Observable<AimsCommonTablePermissions> = this.createGridPerms();
  defaultTravelerType: SchTravelerType = new SchTravelerType();

  ngOnInit() {
    this.transferHttp.getSingle('Sch/GetTravelRequestConfig').subscribe(ret => {
      this.trvlRequestConfig = (ret != null ? ret : new SchTravelRequestConfig());
    });
  }

  // Organization
  public handleOrgRowUpdate(update: TableRowUpdate) {
    const item: Organizations = update.newRow as Organizations;
    this.transferHttp.put('Sch/SaveOrganizations', item).subscribe(_ => {
       this.organizations = this.transferHttp.getSingle('Sch/GetOrganizations');
    });
  }

  // Organization Type
  public handleOrgTypeRowUpdate(update: TableRowUpdate) {
    const item: OrganizationType = update.newRow as OrganizationType;
    this.transferHttp.put('Sch/SaveOrganizationType', item).subscribe(_ => {
       this.organizationType = this.transferHttp.getSingle('Sch/GetOrganizationType');
    });
  }

  // Resource
  public handleResRowUpdate(update: TableRowUpdate) {
    const item: Resources = update.newRow as Resources;
    this.transferHttp.put('Sch/SaveResources', item).subscribe(_ => {
      this.resources = this.transferHttp.getSingle('Sch/GetResources');
    });
  }

  // Schedule Item Type
  public handleSchedItemTypeRowUpdate(update: TableRowUpdate) {
    const item: ScheduleItemType = update.newRow as ScheduleItemType;
    this.transferHttp.put('Sch/SaveScheduleItemType', item).subscribe(_ => {
      this.scheduleItemType = this.transferHttp.getSingle('Sch/GetScheduleItemType');
    });
  }

  colorChangeComplete(event, element) {
    let x = event.color;
    element.color = x.hex;
  }

  // Clearance
  public handleClearanceRowUpdate(update: TableRowUpdate) {
    const item: Clearance = update.newRow as Clearance;
    this.transferHttp.put('Sch/SaveClearance', item).subscribe(_ => {
      this.clearance = this.transferHttp.getSingle('Sch/GetClearance');
    });
  }

  // Sites
  public handleSiteRowUpdate(update: TableRowUpdate) {
    const item: AllSites = update.newRow as AllSites;
    this.transferHttp.put('Sch/SaveTravelSites', item).subscribe(_ => {
      this.travelSites = this.transferHttp.getSingle('Sch/GetScheduleAndTravelSites');
    });
  }

  // Grade
  public handleGradeRowUpdate(update: TableRowUpdate) {
    const item: SchGrade = update.newRow as SchGrade;
    this.transferHttp.put('Sch/SaveGrade', item).subscribe(_ => {
      this.grade = this.transferHttp.getSingle('Sch/GetGrade');
    });
  }

   // Minimum Permanent Positions
  public handleMinPermPosRowUpdate(update: TableRowUpdate) {
    const item: MinPermPos = update.newRow as MinPermPos;
    this.transferHttp.put('Sch/SaveMinPermPos', item).subscribe(_ => {
      this.minPermPos = this.transferHttp.getSingle('Sch/GetMinPermPos');
    });
  }

  // Travel Task Code
  public handleTravelTaskCodeRowUpdate(update: TableRowUpdate) {
    const item: OpsTaskCodes = update.newRow as OpsTaskCodes;
    this.transferHttp.put('Sch/SaveTravelTaskCode', item).subscribe(_ => {
      this.travelTaskCodes = this.transferHttp.getSingle('Sch/GetTravelTaskCodes');
    });
  }

  // Travel Request Email
  public handleTravelRequestEmailRowUpdate(update: TableRowUpdate) {
    const item: SchTravelRequestEmails = update.newRow as SchTravelRequestEmails;
    this.transferHttp.put('Sch/SaveTravelRequestEmail', item).subscribe(_ => {
      this.travelRequestEmails = this.transferHttp.getSingle('Sch/GetTravelRequestEmail');
    });
  }

  public handleTravelRequestEmailRowDelete(deleted: TableRowDelete) {
    const item: SchTravelRequestEmails = deleted.row as SchTravelRequestEmails;
    this.transferHttp.delete(`Sch/DeleteTravelRequestEmail?emailId=${item.externalTravelRequestEmailId}`).subscribe({
      next: _ => {
        this.travelRequestEmails = this.transferHttp.getSingle('Sch/GetTravelRequestEmail');
      },
      error: (e) =>  { this.showSpinner = false; }
    });
  }

  // Lodging Auth Type
  public handleLodgingAuthTypeRowUpdate(update: TableRowUpdate) {
    const item: SchLodgingAuthType = update.newRow as SchLodgingAuthType;
    this.transferHttp.put('Sch/SaveLodgingAuthType', item).subscribe(_ => {
      this.lodgingAuthTypeValues = this.transferHttp.getSingle('Sch/GetLodgingAuthType');
    });
  }

  // Rental Car Auth Type
  public handleRentalCarAuthTypeRowUpdate(update: TableRowUpdate) {
    const item: SchRentalCarAuthType = update.newRow as SchRentalCarAuthType;
    this.transferHttp.put('Sch/SaveRentalCarAuthType', item).subscribe(_ => {
      this.rentalCarAuthTypeValues = this.transferHttp.getSingle('Sch/GetRentalCarAuthType');
    });
  }

  // Flight Auth Type
  public handleFlightAuthTypeRowUpdate(update: TableRowUpdate) {
    const item: SchFlightAuthType = update.newRow as SchFlightAuthType;
    this.transferHttp.put('Sch/SaveFlightAuthType', item).subscribe(_ => {
      this.flightAuthTypeValues = this.transferHttp.getSingle('Sch/GetFlightAuthType');
    });
  }

  // Traveler Type
  public handleTravelerTypeRowUpdate(update: TableRowUpdate) {
    const item: SchTravelerType = update.newRow as SchTravelerType;
    this.transferHttp.put('Sch/SaveTravelerType', item).subscribe(_ => {
      this.travelerTypeValues = this.transferHttp.getSingle('Sch/GetTravelerType');
    });
  }

  public updateConfig() {
    if (this.trvlRequestConfig)
    {
    this.spinnerText = "Saving...";
    this.showSpinner = true;

    this.transferHttp.put('Sch/SaveTravelRequestConfig', this.trvlRequestConfig).subscribe({
      next: (_) => {
        this.showSpinner = false;

        this.transferHttp.getSingle('Sch/GetTravelRequestConfig').subscribe(ret => {
          this.trvlRequestConfig = (ret != null ? ret : new SchTravelRequestConfig());
        });
      },
      error: _ => {
        this.showSpinner = false;
      }
    });
    }
  }

}
